import { Card, Divider, Heading, Text } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren } from 'react';
import type { BillingPlanKey } from '../../../../context/env-context';

const BillingPlanGradients = {
  default: {
    backgroundImage: `
      radial-gradient(at 40% 20%, #ffb87a 0px, transparent 50%),
      radial-gradient(at 80% 0%, #1fddff 0px, transparent 50%),
      radial-gradient(at 0% 50%, #ffdbde 0px, transparent 50%),
      radial-gradient(at 80% 50%, #ff85ad 0px, transparent 50%),
      radial-gradient(at 0% 100%, #ffb58a 0px, transparent 50%),
      radial-gradient(at 80% 100%, #6b66ff 0px, transparent 50%),
      radial-gradient(at 0% 0%, #ff85a7 0px, transparent 50%)
    `,
    borderBottom: '10px solid #ff9443',
  },
  basic_free: {
    backgroundImage: `
      radial-gradient(at 40% 20%, #6366F1 0px, transparent 50%),
      radial-gradient(at 80% 0%, #34D399 0px, transparent 50%),
      radial-gradient(at 0% 50%, #818CF8 0px, transparent 50%),
      radial-gradient(at 80% 50%, #06B6D4 0px, transparent 50%),
      radial-gradient(at 0% 100%, #A78BFA 0px, transparent 50%),
      radial-gradient(at 80% 100%, #2DD4BF 0px, transparent 50%),
      radial-gradient(at 0% 0%, #7C3AED 0px, transparent 50%)`,
    borderBottom: '10px solid #34D399',
  },
  v2_growth: {
    backgroundImage: `
      radial-gradient(at 30% 10%, #7affcc 0px, transparent 50%),
      radial-gradient(at 60% 0%, #1fddff 0px, transparent 50%),
      radial-gradient(at 0% 50%, #eaffdb 0px, transparent 50%),
      radial-gradient(at 39% 148%, #85cbff 0px, transparent 50%),
      radial-gradient(at 0% 19%, #8afff9 0px, transparent 50%),
      radial-gradient(at 80% 100%, #66ffb8 0px, transparent 50%),
      radial-gradient(at 0% 120%, #85b4ff 0px, transparent 50%)`,
    borderBottom: '10px solid #088012',
  },
  v2_pro: {
    backgroundImage: `
      radial-gradient(at 10% 30%, #989898 0px, transparent 50%),
      radial-gradient(at 60% 0%, #d8d8d8 0px, transparent 50%),
      radial-gradient(at 0% 150%, #c0c0c0 0px, transparent 0%),
      radial-gradient(at 39% 148%, #bdc6cd 0px, transparent 50%),
      radial-gradient(at 0% 189%, #c5c5c5 0px, transparent 50%),
      radial-gradient(at 80% 100%, #9c9c9c 0px, transparent 50%),
      radial-gradient(at 0% 20%, #b7bfcc 0px, transparent 120%)`,
    borderBottom: '10px solid #5b36f5',
  },
  enterprise: {
    backgroundImage: `
      linear-gradient(
        45deg,
        #ff61d3 10%,
        #b318ff 36%,
        #b318ff 47%,
        #30b6ff 67%,
        #00f0ff 76%,
        #1e43ff
      )`,
    borderBottom: '10px solid #ff61d3',
  },
};

export function PlanCard(
  props: PropsWithChildren<{
    title: string;
    type: BillingPlanKey;
    price: number | null;
    description: string;
  }>,
) {
  const gradient = (() => {
    switch (props.type) {
      case 'basic_free':
        return BillingPlanGradients.basic_free;
      case 'v2_growth':
        return BillingPlanGradients.v2_growth;
      case 'v2_pro':
        return BillingPlanGradients.v2_pro;
      case 'enterprise':
        return BillingPlanGradients.enterprise;
      default:
        return BillingPlanGradients.default;
    }
  })();

  const image = (() => {
    switch (props.type) {
      case 'v2_pro':
        return 'https://www.sparklayer.io/assets/images/icons/integrate3-dark.png';
      case 'enterprise':
        return 'https://i.ibb.co/Zhv4FL8/plan-enterprise.png';
      default:
        return 'https://www.sparklayer.io/assets/images/icons/integrate3.png';
    }
  })();

  return (
    <div className="flex-1 basis-50 flex-col">
      <Card hidePadding fullHeight>
        <div className="flex w-full items-center p-6 align-middle" style={gradient}>
          <img
            src={image}
            alt={`${props.title} sparklayer logo`}
            className="m-auto h-22.5 w-22.5"
          />
        </div>
        <div className="flex flex-col gap-2 px-5 pt-4 pb-2">
          <Heading level="h3" style="md" weight="semibold">
            {props.title}
          </Heading>
          {props.price !== null ? (
            <div className="flex flex-row gap-1">
              <Text size="xl">${props.price}</Text>
              <Text variant="secondary">
                {t('features:account.components.plan_card.default_billing_duration')}
              </Text>
            </div>
          ) : (
            <Text size="xl">{t('features:account.components.plan_card.custom_price')}</Text>
          )}

          <Text variant="secondary">{props.description}</Text>
        </div>
        <Divider />
        <div className="flex flex-col gap-2 px-5 pt-2 pb-4">{props.children}</div>
      </Card>
    </div>
  );
}
